/*
 * Copyright (c) 2024, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {Button, Stack, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import FieldsetForm from '@app/components/shared/ui/FieldsetForm'

const PasswordlessEmailConfirmation = ({form, submitForm, email = ''}) => {
    const {formatMessage} = useIntl()

    return (
        <FieldsetForm
            legendText={formatMessage({
                defaultMessage: 'Passwordless sign-in link resend form',
                id: 'passwordless_form.resend_form.legend'
            })}
            isMandatoryNote={false}
            onSubmit={form.handleSubmit(submitForm)}
            error={form.formState.errors?.global?.message}
        >
            <Stack spacing={24}>
                <Stack spacing={6}>
                    <Text variant="bodyLarge1" role="heading" aria-level="2">
                        <FormattedMessage
                            defaultMessage="Check Your Email"
                            id="auth_modal.check_email.title.check_your_email"
                        />
                    </Text>
                    <Stack spacing={10}>
                        <Text variant="bodyLarge2">
                            <FormattedMessage
                                defaultMessage="We just sent a login link to <b>{email}</b>"
                                id="auth_modal.check_email.description.just_sent"
                                values={{
                                    email: email,
                                    b: (chunks) => <b>{chunks}</b>
                                }}
                            />
                        </Text>
                        <Text variant="bodyLarge2">
                            <FormattedMessage
                                defaultMessage="The link may take a few minutes to arrive, check your spam folder if you're having trouble finding it"
                                id="auth_modal.check_email.description.check_spam_folder"
                            />
                        </Text>
                    </Stack>
                </Stack>
                <Button type="submit" isLoading={form.formState.isSubmitting} variant="primary">
                    <FormattedMessage
                        defaultMessage="Resend Link"
                        id="auth_modal.check_email.button.resend_link"
                    />
                </Button>
            </Stack>
        </FieldsetForm>
    )
}

PasswordlessEmailConfirmation.propTypes = {
    form: PropTypes.object,
    submitForm: PropTypes.func,
    email: PropTypes.string
}

export default PasswordlessEmailConfirmation
